import React, { useEffect, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import useSound from "../../hooks/common/useSound";
import useAppTranslation from "../../hooks/useAppTranslation";
import { getLocalizedAssetPath } from "../../utils/getLocalizedLogo";

export default function Flipbook({
  index,
  story,
  setStory,
  pageCount,
  setShowFinalModal,
  setIsFlipping,
  handleChangeBackground,
  setIndex,
  isReadOnly = false,
}) {
  const { t } = useAppTranslation("components/flipbook/flipbook");
  const bookRef = useRef();
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 400, height: 600 });
  const [currentPage, setCurrentPage] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [tempText, setTempText] = useState("");
  const [localIsFlipping, setLocalIsFlipping] = useState(false);
  const editRef = useRef(null);
  const playFlipSound = useSound("/audio/customBook/flip.mp3");

  const handleSave = () => {
    if (editingIndex === null) return;

    setStory((prevStory) => {
      const newStory = [...prevStory];
      if (newStory[editingIndex]) {
        newStory[editingIndex] = {
          ...newStory[editingIndex],
          page_content: tempText,
          contents: tempText,
        };
      }
      return newStory;
    });
    setEditingIndex(null);
    setTempText("");
  };

  useEffect(() => {
    const resize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setSize({
        width: Math.floor(rect.width / 2),
        height: Math.floor(rect.height),
      });
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // 외부(Reading.js)에서 인덱스를 -1(처음)로 바꿨을 때 플립북 표지로 리셋
  useEffect(() => {
    if (index === -1 && bookRef.current?.pageFlip()) {
      bookRef.current.pageFlip().flip(0);
    }
  }, [index]);

  return (
    <div ref={containerRef} className="flipbook-container">
      {size.width > 0 && size.height > 0 && (
        <HTMLFlipBook
          ref={bookRef}
          width={size.width}
          height={size.height}
          size="stretch"
          maxShadowOpacity={0.4}
          showPageCorners={false}
          disableFlipByClick={true} // 자동 클릭 영구 비활성화
          useMouseEvents={editingIndex === null}
          swipeDistance={30}
          mobileScrollSupport
          onFlip={(e) => {
            setCurrentPage(e.data);
            // 표지 페이지 제거로 인해 인덱스 보정 불필요
            const baseIndex = Math.floor(e.data / 2);
            if (setIndex) setIndex(baseIndex);

            if (setIsFlipping) setIsFlipping(true);
          }}
          onChangeState={(e) => {
            // 라이브러리 상태 추적
            if (e.data === "flipping") {
              playFlipSound();
              setLocalIsFlipping(true);
              if (setIsFlipping) setIsFlipping(true);
            }
            if (e.data === "read") {
              setLocalIsFlipping(false);
              if (setIsFlipping) setIsFlipping(false);
            }
          }}
        >
          {/* 모든 페이지를 하나의 배열로 합쳐서 렌더링 */}
          {story.flatMap((c, idx) => [
            <div key={`img-${idx}`} className="page image-page">
              <div className="page-wrap">
                {editingIndex === null && (
                  <div
                    className="click-overlay"
                    onClick={(e) => {
                      e.stopPropagation();
                      bookRef.current?.pageFlip()?.flipPrev();
                    }}
                  />
                )}
                <img src={c.image_url || c.background} alt={`chapter-${idx}`} />
              </div>
            </div>,
            <div key={`txt-${idx}`} className="page">
              <div className="text-page">
                {editingIndex === null && (
                  <div
                    className="click-overlay"
                    onClick={(e) => {
                      e.stopPropagation();
                      // 마지막 페이지일 경우 최종 모달 노출, 아니면 다음으로
                      if (idx === story.length - 1) {
                        setShowFinalModal(true);
                      } else {
                        bookRef.current?.pageFlip()?.flipNext();
                      }
                    }}
                  />
                )}
                <p>{c.page_content}</p>
                <span className="page-number">
                  {c.page_id}/{pageCount}
                </span>
              </div>
            </div>,
          ])}
        </HTMLFlipBook>
      )}

      {editingIndex !== null && (
        <div
          className="editing-overlay"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <div className="edit-box">
            <h3>{t("edit.title")}</h3>
            <textarea
              ref={editRef}
              autoFocus
              value={tempText}
              onChange={(e) => setTempText(e.target.value)}
              className="edit-textarea"
              onKeyDown={(e) => e.stopPropagation()}
            />
            <div className="btn-group">
              <button
                className="save-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
              >
                {t("actions.save")}
              </button>
              <button
                className="cancel-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingIndex(null);
                  setTempText("");
                }}
              >
                {t("actions.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isReadOnly && (
        <>
          <figure
            className={`change-image-btn ${localIsFlipping ? "flipping" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (handleChangeBackground) handleChangeBackground();
            }}
          >
            <img
              src={getLocalizedAssetPath("/images/makingBranch/change-btn.svg")}
              alt={t("alts.changeImage")}
            />
          </figure>
          <figure
            className={`change-content-btn ${localIsFlipping ? "flipping" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              const pageIdx = Math.floor(currentPage / 2);
              setEditingIndex(pageIdx);
              setTempText(story[pageIdx]?.page_content || "");
            }}
          >
            <img
              src={getLocalizedAssetPath("/images/makingBranch/update-btn.svg")}
              alt={t("alts.updateContent")}
            />
          </figure>
        </>
      )}
    </div>
  );
}
